import { mapValues } from 'lodash'
import { schema } from 'prose-core'
import { baseKeymap, createParagraphNear, deleteSelection, selectNodeBackward } from 'prosemirror-commands'
import { redo, undo } from 'prosemirror-history'
import { undoInputRule } from 'prosemirror-inputrules'
import { Transaction } from 'prosemirror-state'
import { goToNextCell } from 'prosemirror-tables'
import { setDocumentSettingsModalVisible } from 'redux/actions'
import store from 'redux/store'
import {
  Command,
  INDENT_DIRECTION,
  breakOutOfInlineCode,
  breakOutOfList,
  changeIndentation,
  deleteEmptyNode,
  deleteEmptyToggle,
  deleteFirstCodeCharacter,
  deleteZeroWidthSpaceWithoutInlineCode,
  joinListsByDeletion,
  joinListsFromFirstListItem,
  mergeBackwardsIntoListItem,
  preventDefault,
  preventToggleBlockSelectBackward,
  preventZeroWidthSpaceDelete,
  splitListItemOnGapCursor,
  splitToggleListItem,
  splitVoteListItem,
  turnIntoList,
  unindentFromStart,
} from '../Commands'
import { showCommentPrompt } from '../Commands/InlineFormatBar/showCommentPrompt'
import { showLinkPrompt } from '../Commands/InlineFormatBar/showLinkPrompt'
import { cancelFocusMode } from '../Commands/cancelFocusMode'
import { maybeDeleteColumnItem } from '../Commands/columns'
import { INLINE_DIRECTION } from '../Commands/constants'
import { joinBackward } from '../Commands/joinBackward'
import { maybeLiftEmptyBlock } from '../Commands/maybeLiftEmptyBlock'
import { PredictCommandMode, maybeApplyAIPrediction, maybeRejectAIPrediction, predictAI } from '../Commands/predictAI'
import { preventTableSelectionDelete } from '../Commands/preventTableSelectionDelete'
import { pullNextItem } from '../Commands/pullNextItem'
import { setBlockType } from '../Commands/setBlockType'
import { splitBlock } from '../Commands/splitBlock'
import { splitListItem } from '../Commands/splitListItem'
import { splitTaskListItem } from '../Commands/splitTaskListItem'
import {
  TABLE,
  addRowAfter,
  deleteCellContents,
  moveDownInTable,
  moveDownIntoTable,
  moveLeftInTable,
  moveLeftIntoTable,
  moveRightInTable,
  moveRightIntoTable,
  moveUpInTable,
  moveUpIntoTable,
  selectDownInTable,
  selectLeftInTable,
  selectRightInTable,
  selectUpInTable,
} from '../Commands/table'
import { maybeDeleteColumnOrRow } from '../Commands/table/mutation'
import { maybeSelectTable } from '../Commands/table/selection'
import { toggleToggleList } from '../Commands/toggleToggleList'
import { toggleMark } from '../Toolbar/components/Format/components/utils/toggleMark'
import { KeyhandlerMetadataKey } from '../constants'
import { KeyhandlerMetadata } from '../types'

interface KeydownConfig {
  [keydown: string]: Record<string, Command>
}

/**
 * This will returning something that `import { keymap } from 'prosemirror-keymap'` can handle.
 *
 * It does so by enforcing naming and implementing the same behavior as `chainCommands` but with a custom `dispatch` that sets up metadata.
 *  */
const getNamedCommands = (keydownConfig: KeydownConfig): Record<string, Command> => {
  return mapValues(keydownConfig, (namedHandlers: Record<string, Command>, keydown): Command => {
    // Extract the metadata from the definition

    const commands = Object.entries(namedHandlers).map(([handlerName, command]) => {
      return { command, keydown, handlerName }
    })

    // Takes over chainCommands
    const command: Command = function (state, dispatch, view) {
      for (const { command, keydown, handlerName } of commands) {
        const keyhandlerMetadata: KeyhandlerMetadata = { keydown, handlerName }
        // This custom dispatch will add the command handler metadata to the transaction
        // We need it for tracked changes
        const newDispatch = dispatch
          ? (tr: Transaction) => {
              tr.setMeta(KeyhandlerMetadataKey, keyhandlerMetadata)
              dispatch(tr)
            }
          : undefined
        if (command(state, newDispatch, view)) {
          console.debug(`Command handled by: ${keydown} - ${handlerName}`) // eslint-disable-line no-console
          return true
        }
      }
      if (dispatch) {
        const tr = state.tr.setMeta(KeyhandlerMetadataKey, { keydown, handlerName: 'default' })
        dispatch(tr)
      }
      return false
    }

    return command
  })
}

const namedCommands = getNamedCommands({
  ArrowDown: { moveDownIntoTable, moveDownInTable },
  ArrowLeft: {
    breakOutOfInlineCode: breakOutOfInlineCode('left'),
    deleteZeroWidthSpaceWithoutInlineCode: deleteZeroWidthSpaceWithoutInlineCode('left'),
    moveLeftIntoTable,
    moveLeftInTable,
  },
  ArrowRight: {
    breakOutOfInlineCode: breakOutOfInlineCode('right'),
    deleteZeroWidthSpaceWithoutInlineCode: deleteZeroWidthSpaceWithoutInlineCode('right'),
    moveRightIntoTable,
    moveRightInTable,
  },
  ArrowUp: {
    moveUpIntoTable,
    moveUpInTable,
  },
  Backspace: {
    preventZeroWidthSpaceDelete,
    maybeSelectTableBackwards: maybeSelectTable(INLINE_DIRECTION.BACKWARDS),
    maybeDeleteColumnOrRow,
    deleteCellContents,
    preventTableSelectionDelete,
    joinListsByDeletion,
    joinListsFromFirstListItem,
    breakOutOfList,
    unindentFromStart: unindentFromStart(),
    maybeDeleteColumnItem, // must be before deleteSelection
    deleteSelection,
    deleteEmptyNode,
    deleteEmptyToggle,
    mergeBackwardsIntoListItem,
    joinBackward,
    preventToggleBlockSelectBackward,
    selectNodeBackward,
  },
  Delete: {
    pullNextItem,
    deleteFirstCodeCharacter,
    maybeSelectTable: maybeSelectTable(INLINE_DIRECTION.FORWARDS),
    maybeDeleteColumnOrRow,
    deleteCellContents,
    preventTableSelectionDelete,
    joinListsByDeletion,
    joinListsFromFirstListItem,
    maybeDeleteColumnItem, // must be before deleteSelection
    deleteSelection,
    deleteEmptyNode,
    mergeBackwardsIntoListItem,
  },
  Escape: {
    cancelFocusMode,
  },
  Enter: {
    splitToggleListItem,
    unindentFromStartIfEmpty: unindentFromStart(true),
    splitListItemOnGapCursor,
    splitTaskListItem,
    splitListItem: splitListItem(schema.nodes.list_item),
    splitVoteListItem,
    maybeLiftEmptyBlock,
    createParagraphNear,
    splitBlock,
  },
  Tab: {
    maybeApplyAIPrediction,
    changeIndentation: changeIndentation(INDENT_DIRECTION.INDENT),
    goToNextCell: goToNextCell(TABLE.NEXT_CELL),
    addRowAfter,
    preventDefault,
  },
  'Shift-ArrowDown': { selectDownInTable },
  'Shift-ArrowLeft': { selectLeftInTable },
  'Shift-ArrowRight': { selectRightInTable },
  'Shift-ArrowUp': { selectUpInTable },
  'Shift-Backspace': { joinBackward },
  'Shift-Enter': { splitBlock },
  'Shift-Tab': {
    changeIndentation: changeIndentation(INDENT_DIRECTION.UNINDENT),
    goToNextCell: goToNextCell(TABLE.PREVIOUS_CELL),
    preventDefault,
  },
  'Mod-z': {
    undoInputRule,
    undo,
  },
  'Mod-y': { redo },
  'Shift-Mod-z': { redo },
  'Mod-]': { changeIndentation: changeIndentation(INDENT_DIRECTION.INDENT) },
  'Mod-[': { changeIndentation: changeIndentation(INDENT_DIRECTION.UNINDENT) },
  'Mod-b': { toggleMarkStrong: toggleMark(schema.marks.strong) },
  'Mod-e': { toggleMarkCode: toggleMark(schema.marks.code) },
  'Mod-i': { toggleMarkEm: toggleMark(schema.marks.em) },
  'Mod-u': { toggleMarkUnderline: toggleMark(schema.marks.underline) },
  'Mod-Shift--': { toggleStrikethrough: toggleMark(schema.marks.strike) },
  'Mod-k': { showLinkPrompt },
  'Mod-m': { showCommentPrompt },
  'Mod-Alt-1': { setBlockTypeH1: setBlockType('heading1') },
  'Mod-Alt-2': { setBlockTypeH2: setBlockType('heading2') },
  'Mod-Alt-3': { setBlockTypeH3: setBlockType('heading3') },
  'Mod-Alt-0': { setBlockTypeNormal: setBlockType('normal') },
  'Mod-Shift-7': { turnIntoOrderedList: turnIntoList(schema.nodes.ordered_list) },
  'Mod-Shift-8': { turnIntoUnorderedList: turnIntoList(schema.nodes.unordered_list) },
  'Mod-Shift-9': { turnIntoCheckList: turnIntoList(schema.nodes.check_list) },
  'Mod-Alt-s': {
    openSettingsModal: () => !!store.dispatch(setDocumentSettingsModalVisible(true)),
  },
  'Mod-Shift-a': { maybeApplyAIPrediction },
  'Mod-Shift-d': { maybeRejectAIPrediction },
  'Mod-Enter': { toggleToggleList, predictAI: predictAI(PredictCommandMode.manual) },
})

// Alphabetical order ignoring modifiers
export const Keymap: Record<string, Command> = {
  ...baseKeymap,
  ...namedCommands,
}
